
import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2, BookOpen, Palette, Music } from './Icons';

interface LoadingScreenProps {
  progress: number;
  message: string;
}

const QUOTES = [
  "Firing up the multi-core magic forge...",
  "Painting all pages at once...",
  "Synthesizing multiple voices simultaneously...",
  "Weaving your story in hyper-speed...",
  "Consulting the lightning-fast library...",
  "Streaming magic from the cloud...",
  "Polishing every chapter in parallel...",
  "Gathering stardust at light-speed...",
  "Crafting a personalized masterpiece...",
  "Readying your adventure..."
];

const LoadingScreen: React.FC<LoadingScreenProps> = ({ progress, message }) => {
  const [quoteIdx, setQuoteIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setQuoteIdx((prev) => (prev + 1) % QUOTES.length);
    }, 2500); // Faster cycle for faster generation
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center p-6 text-center overflow-hidden">
      {/* Immersive Background */}
      <div className="absolute inset-0 bg-[#0f0e17] z-[-2]"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-spark-900/40 via-transparent to-black z-[-1]"></div>
      
      {/* Animated Elements */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold-500/10 rounded-full blur-[120px] animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-forge-500/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '2s' }}></div>

      <div className="max-w-md w-full space-y-12 animate-fade-in">
        {/* Animated Icon Group */}
        <div className="relative h-24 flex justify-center items-center">
            <div className="absolute animate-float">
                <BookOpen className="w-16 h-16 text-gold-400 opacity-20" />
            </div>
            <div className="absolute animate-spin-slow">
                <div className="w-32 h-32 border-2 border-dashed border-white/5 rounded-full"></div>
            </div>
            <Loader2 className="w-10 h-10 text-white animate-spin relative z-10" />
        </div>

        <div className="space-y-4">
            <h2 className="text-3xl font-serif font-bold text-white tracking-tight">Rapid Forging</h2>
            <p className="text-gold-400 font-hand text-2xl h-8 italic transition-all duration-1000">
                {QUOTES[quoteIdx]}
            </p>
        </div>

        {/* Progress Container */}
        <div className="space-y-6">
            <div className="relative h-3 w-full bg-white/5 rounded-full overflow-hidden border border-white/10 p-[2px]">
                <div 
                    className="h-full bg-gradient-to-r from-gold-500 via-forge-500 to-spark-500 rounded-full transition-all duration-500 ease-out shadow-[0_0_15px_rgba(222,160,30,0.5)]"
                    style={{ width: `${Math.max(5, progress)}%` }}
                ></div>
            </div>
            
            <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                <span className="flex items-center gap-2">
                    <Palette className="w-3 h-3" />
                    Parallel Art
                </span>
                <span className="text-white text-sm tabular-nums">{Math.round(progress)}%</span>
                <span className="flex items-center gap-2">
                    <Music className="w-3 h-3" />
                    Audio Sync
                </span>
            </div>
        </div>

        <p className="text-xs text-gray-500 leading-relaxed max-w-xs mx-auto">
            We are processing multiple pages at once to <br/>
            bring your story to life faster than ever.
        </p>
      </div>
    </div>
  );
};

export default LoadingScreen;
